import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { motion, useReducedMotion } from "motion/react";

import { promotionCopy } from "./agentLaunchPromotionCopy";
import { useAgentLaunch } from "./AgentLaunchProvider";
import { AgentLaunchClaim } from "./components/AgentLaunchClaim";
import { BetaIntroThumbnail } from "./components/BetaIntroThumbnail";

type PromotionSource = keyof typeof promotionCopy | "settings";

const settingsPromotionCopy = {
  projectGeneral: {
    title: "Build the whole site, not one page at a time",
    description:
      "Create the homepage, product pages, collections, and shared navigation from one conversation.",
    gradient:
      "bg-gradient-to-r from-sheet via-sky-soft to-violet-soft md:flex-row",
  },
  abTesting: {
    title: "Build the next test from live campaign data",
    description:
      "Read Meta performance, pull the winning creative, and build a matching landing-page variant.",
    gradient:
      "bg-gradient-to-r from-sheet via-violet-soft to-pink-soft md:flex-row",
  },
  integrations: {
    title: "Use the data inside your connected tools",
    description:
      "Read Shopify products, Klaviyo lists and campaigns, Meta ads, and analytics while you build.",
    gradient:
      "bg-gradient-to-r from-sheet via-cyan-soft to-brand-yellow/30 md:flex-row",
  },
  advanced: {
    title: "Change the whole site in one request",
    description:
      "Update shared navigation, metadata, tracking, and consent rules without editing each page.",
    gradient:
      "bg-gradient-to-r from-sheet via-orange-soft to-violet-soft md:flex-row",
  },
  fonts: {
    title: "Apply one brand system across the whole site",
    description:
      "Set the colors, fonts, logo, and assets once. Replo carries them across every page.",
    gradient:
      "bg-gradient-to-r from-sheet via-pink-soft to-orange-soft md:flex-row",
  },
  scripts: {
    title: "Install tracking across the whole site",
    description:
      "Add GA4, GTM, Meta, TikTok, and consent rules without pasting scripts page by page.",
    gradient:
      "bg-gradient-to-r from-sheet via-sky-soft to-cyan-soft md:flex-row",
  },
  analytics: {
    title: "Ask the question, not the dashboard filter",
    description:
      "Compare revenue, conversion, products, pages, and traffic sources with a plain-English request.",
    gradient:
      "bg-gradient-to-r from-sheet via-cyan-soft to-violet-soft md:flex-row",
  },
  workspace: {
    title: "One conversation can run the launch",
    description:
      "Research the market, build the site, connect checkout, publish it, and read the sales data.",
    gradient:
      "bg-gradient-to-r from-sheet via-violet-soft to-cyan-soft md:flex-row",
  },
  members: {
    title: "Hand off an outcome, not a list of edits",
    description:
      "Research the angle, build the page, connect the tools, and verify the published result.",
    gradient:
      "bg-gradient-to-r from-sheet via-pink-soft to-violet-soft md:flex-row",
  },
  account: {
    title: "Research, build, and publish in one conversation",
    description:
      "Go from competitor research to a live, editable storefront without switching tools.",
    gradient:
      "bg-gradient-to-r from-sheet via-sky-soft to-violet-soft md:flex-row",
  },
  referrals: {
    title: "Turn artwork into a product customers can buy",
    description:
      "Choose a Printful product, place the design, render mockups, set the price, and publish the store.",
    gradient:
      "bg-gradient-to-r from-sheet via-brand-yellow/30 to-brand-light-pink/60 md:flex-row",
  },
} as const;

export type SettingsPromotionContext = keyof typeof settingsPromotionCopy;

type AgentLaunchPromotionProps =
  | {
      source: "analytics";
      analyticsContinuation?: "insights" | "integrations";
      onContinueWithoutAgent?: () => void;
      settingsContext?: never;
    }
  | {
      source: "settings";
      settingsContext: SettingsPromotionContext;
      analyticsContinuation?: never;
      onContinueWithoutAgent?: never;
    }
  | {
      source: Exclude<PromotionSource, "analytics" | "settings">;
      settingsContext?: never;
      analyticsContinuation?: never;
      onContinueWithoutAgent?: never;
    };

export function AgentLaunchPromotion({
  source,
  settingsContext,
  analyticsContinuation,
  onContinueWithoutAgent,
}: AgentLaunchPromotionProps) {
  const launch = useAgentLaunch();
  const logEvent = useLogAnalytics();
  const shouldReduceMotion = useReducedMotion();
  const grainFilterId = `agent-launch-grain-${React.useId().replace(/:/g, "")}`;
  const isSidebar = source === "sidebar";
  const isPersistent = source === "pages";
  const copy =
    source === "settings"
      ? settingsPromotionCopy[settingsContext]
      : promotionCopy[source];

  if (!launch?.isAvailable) {
    return null;
  }

  const openWelcome = () => {
    logEvent("agent.beta.promotion.clicked", { source });
    launch.openWelcome(
      source,
      source === "analytics"
        ? {
            variant: "analytics",
            analyticsContinuation,
            onNotNow: onContinueWithoutAgent,
          }
        : undefined,
    );
  };

  const testId =
    source === "settings"
      ? `agent-launch-promotion-settings-${settingsContext}`
      : `agent-launch-promotion-${source}`;
  const promotionClassName = cn(
    "relative flex w-full flex-col overflow-hidden rounded-xl border-0.5 border-border shadow-xs",
    isSidebar ? "max-h-60" : "max-h-60 md:h-40",
    copy.gradient,
  );
  const animationProps = {
    onViewportEnter: () => {
      logEvent("agent.beta.promotion.viewed", { source });
    },
    viewport: { once: true },
    initial: shouldReduceMotion ? false : { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: shouldReduceMotion ? 0 : DURATION.standard,
      ease: EASING.outQuint,
    },
  };
  const content = (
    <>
      {!isSidebar && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 size-full opacity-20 mix-blend-multiply"
        >
          <filter id={grainFilterId}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.8"
              numOctaves="3"
              stitchTiles="stitch"
            />
          </filter>
          <rect width="100%" height="100%" filter={`url(#${grainFilterId})`} />
        </svg>
      )}
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col items-start",
          isSidebar ? "gap-2.5 p-3" : "justify-center gap-2.5 p-3 md:px-4",
        )}
      >
        <div className="flex w-full flex-col gap-1">
          <h2
            className={cn(
              "font-archivo font-semibold leading-tight text-default",
              isSidebar ? "text-base" : "text-xl",
            )}
          >
            {copy.title}
          </h2>
          {copy.description === null ? (
            <AgentLaunchClaim className="font-archivo text-sm font-medium leading-tight text-muted" />
          ) : (
            <p
              className={cn(
                "leading-relaxed text-muted",
                isSidebar ? "text-xs" : "max-w-xl text-sm",
              )}
            >
              {copy.description}
            </p>
          )}
        </div>
        {!isSidebar && (
          <div className="flex flex-col items-start gap-1.5">
            <Button size="base" onClick={openWelcome}>
              Enable Replo Agent
            </Button>
            {isPersistent && (
              <p className="max-w-lg text-[11px] leading-tight text-placeholder">
                Creates a new project. None of your existing projects are
                impacted.
              </p>
            )}
          </div>
        )}
      </div>
      {!isSidebar && (
        <BetaIntroThumbnail
          onClick={openWelcome}
          imageClassName="object-cover"
          className="hidden aspect-auto shrink-0 overflow-hidden rounded-lg shadow-sm md:order-last md:m-2 md:ml-0 md:block md:h-36 md:w-64"
        />
      )}
    </>
  );

  if (isSidebar) {
    return (
      <motion.button
        {...animationProps}
        type="button"
        aria-label="Open the new Replo introduction"
        onClick={openWelcome}
        className={cn(
          promotionClassName,
          "cursor-pointer appearance-none text-left outline-none transition-shadow duration-fast hover:shadow-sm active:shadow-none focus-visible:ring-2 focus-visible:ring-focus",
        )}
        data-testid={testId}
      >
        {content}
      </motion.button>
    );
  }

  return (
    <motion.section
      {...animationProps}
      aria-label="Try the new Replo"
      className={promotionClassName}
      data-testid={testId}
    >
      {content}
    </motion.section>
  );
}
